import React, { memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Copy, X } from 'lucide-react';
import { SplitPrompt } from '@/types';
import { formatBlockLabel, resolveBlockDisplayTitle } from '../../hooks/useDetailPageActions';

/** 品牌色（与 PlannerResultPanel 同源） */
const ACCENT = '#6D5BF5';

const UNIT_TYPE_LABELS: Record<SplitPrompt['unitType'], string> = {
  S: '首屏',
  D: '详情',
  T: '尾屏',
};

interface SplitPromptFullscreenViewProps {
  /** 全量分屏区块：顶栏按此渲染页签，面板内可直接切换，无需关闭再点下一区块 */
  prompts: SplitPrompt[];
  /** 当前展示区块的 index（对应 SplitPrompt.index） */
  activeIndex: number;
  onSelect: (index: number) => void;
  onClose: () => void;
}

/** 分屏提示词查看面板：面板框架与策划架构查看面板（PlannerResultPanel）一致——
 *  深灰全屏 + 入场动画 + 68px 页签顶栏（各区块为页签）+ 圆形关闭按钮 + 限宽内容区。
 *  文字选择复制与策划面板对齐：内容区 select-text 覆盖全局 body user-select:none（策划面板经
 *  renderFormattedMessage 的 select-text 容器获得同能力），另提供一键复制按钮复制提示词原文。
 *  内容区保留纯文本逐字渲染：提示词是复制使用的原始素材，Markdown 渲染会折叠换行、转义特殊符号，
 *  纯文本保证拖选/Ctrl+C 复制出的内容与原文逐字一致 */
export const SplitPromptFullscreenView: React.FC<SplitPromptFullscreenViewProps> = memo(
  ({ prompts, activeIndex, onSelect, onClose }) => {
    const activeBlock = prompts.find((p) => p.index === activeIndex) ?? prompts[0];

    const [copied, setCopied] = useState(false);
    const copiedTimerRef = useRef<number | null>(null);

    // 切换区块后复位「已复制」反馈：按钮状态应始终对应当前区块的复制结果
    useEffect(() => {
      setCopied(false);
    }, [activeBlock?.index]);

    // 卸载时清理「已复制」反馈定时器
    useEffect(() => {
      return () => {
        if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
      };
    }, []);

    // 一键复制当前区块提示词原文：clipboard API 优先，非安全上下文/权限拒绝时回退 execCommand（与 UserMenu 同款兜底）
    const handleCopy = async () => {
      if (!activeBlock) return;
      const text = activeBlock.content;
      try {
        await navigator.clipboard.writeText(text);
      } catch {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopied(true);
      if (copiedTimerRef.current !== null) window.clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = window.setTimeout(() => setCopied(false), 2000);
    };

    // Esc 关闭；点击背景不关闭（防误触丢失阅读位置）。
    // 满屏面板期间在 capture 阶段处理画布全局快捷键（全局钩子 useKeyboardShortcuts 挂 window bubble
    // 且不感知面板：无条件 Ctrl+A 全选节点、有选中时 Ctrl+C 复制节点，不阻断会在用户不可见时操作画布）。
    // Ctrl+A / Ctrl+C 放行浏览器默认（全选/复制面板文字），仅 stopImmediatePropagation 阻断画布钩子；
    // 不能用 panelRef.contains(e.target) 判定「按键发生在面板内」——面板文字不可聚焦，keydown 的
    // target 恒为 body，contains 判断永远为 false（上一版放行失效的根因）。
    // 其余快捷键（Ctrl+Z 撤销 / Ctrl+Y 重做 / Ctrl+V 粘贴 / Ctrl+D 原地复制 / Delete/Backspace 删除）
    // 在面板内无合理默认行为，preventDefault + 阻断，防止面板打开期间回滚/删除画布
    useEffect(() => {
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
          return;
        }
        const ctrl = e.ctrlKey || e.metaKey;
        const key = e.key.toLowerCase();
        if (ctrl && (key === 'a' || key === 'c')) {
          e.stopImmediatePropagation();
          return;
        }
        if (
          e.key === 'Delete' ||
          e.key === 'Backspace' ||
          (ctrl && ['z', 'y', 'v', 'd'].includes(key))
        ) {
          e.preventDefault();
          e.stopImmediatePropagation();
        }
      };
      window.addEventListener('keydown', onKeyDown, true);
      return () => window.removeEventListener('keydown', onKeyDown, true);
    }, [onClose]);

    if (!activeBlock) return null;

    const displayTitle = resolveBlockDisplayTitle(activeBlock);

    return createPortal(
      <div
        className="fixed inset-0 z-[999] bg-[#1c1c1e] overflow-hidden"
        style={{ animation: 'planner-panel-in 200ms ease-out' }}
      >
        {/* 内容层：动画（含 transform）留在外层根元素，避免 transform 属性冲突 */}
        <div className="flex h-full w-full flex-col">
        <style>{`@keyframes planner-panel-in { from { opacity: 0; transform: scale(0.97); } to { opacity: 1; transform: scale(1); } }`}</style>

        {/* 单排顶栏：无标题，各区块页签全宽水平居中（垂直中心对齐）；关闭按钮绝对定位居右，不挤占页签居中 */}
        <div className="relative shrink-0 h-[68px] flex items-stretch justify-center border-b border-white/[0.08]">
          <div className="flex items-stretch justify-center gap-10">
            {prompts.map((block, i) => {
              const active = block.index === activeBlock.index;
              return (
                <button
                  key={block.index}
                  type="button"
                  onClick={() => onSelect(block.index)}
                  title={UNIT_TYPE_LABELS[block.unitType]}
                  className={`relative flex items-center gap-2 text-[19px] transition-colors duration-200 shrink-0 ${
                    active ? 'text-white' : 'text-white/45 hover:text-white/75'
                  }`}
                >
                  <span
                    className="text-[14px] font-medium tabular-nums text-white/30 transition-colors duration-200"
                    style={active ? { color: ACCENT } : undefined}
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  {formatBlockLabel(block.index)}
                  <span className="px-2 h-6 rounded-full bg-white/[0.08] text-[13px] text-white/55 flex items-center">
                    {block.unitType}
                  </span>
                  {active && (
                    <span
                      className="absolute left-0 right-0 -bottom-px h-[2px] rounded-full"
                      style={{ background: ACCENT }}
                    />
                  )}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="absolute right-6 top-1/2 -translate-y-1/2 shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/[0.08] transition-colors"
          >
            <X size={22} />
          </button>
        </div>

        {/* key 按区块重挂载：切换页签时滚动位置归顶；限宽阅读度量与策划面板一致 */}
        <div key={activeBlock.index} className="flex-1 min-h-0 overflow-y-auto px-8 pt-6 pb-10">
          <div className="max-w-[1080px] mx-auto">
            <div className="flex items-center gap-3 mb-5">
              <span
                className="w-8 h-8 rounded-full flex items-center justify-center text-[18px] font-medium shrink-0"
                style={{ background: `${ACCENT}26`, color: ACCENT }}
                title={UNIT_TYPE_LABELS[activeBlock.unitType]}
              >
                {activeBlock.unitType}
              </span>
              <span className="text-[22px] font-medium text-white/90 truncate min-w-0">
                {formatBlockLabel(activeBlock.index)}
                {displayTitle && (
                  <span className="font-normal text-white/45"> · {displayTitle}</span>
                )}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                title="复制提示词全文"
                className={`ml-auto shrink-0 h-9 px-4 rounded-full flex items-center gap-1.5 text-[14px] transition-colors ${
                  copied
                    ? 'text-emerald-400 bg-emerald-400/10'
                    : 'text-white/60 hover:text-white bg-white/[0.06] hover:bg-white/[0.12]'
                }`}
              >
                {copied ? <Check size={15} /> : <Copy size={15} />}
                {copied ? '已复制' : '复制全文'}
              </button>
            </div>
            <div className="text-[15px] leading-relaxed text-white/85 whitespace-pre-wrap break-words select-text cursor-text">
              {activeBlock.content}
            </div>
          </div>
        </div>
        </div>
      </div>,
      document.body,
    );
  },
);
